import {useEffect, useRef} from 'react'
import {EntityCardLink} from '@/components/shared/EntityCardLink'
import {useParams} from 'react-router-dom'
import {Bell, ExternalLink} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {SmallText} from '@/components/shared/SmallText'

import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {Badge} from '@/components/ui/badge'
import {formatTimestamp} from '@/lib/date-utils'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {useMarkRead, useNotification} from './useNotifications'
import {getNotificationRoute} from './notificationNavigation'
import {getEventTypeBadgeVariant, getEventTypeLabel} from './notificationUtils'
import {PageLayout} from '@/components/shared/PageLayout'
import {Icon} from '@/components/shared/Icon'
import {type NotificationWithData} from "@/types/notification";
import {UseQueryResult} from "@tanstack/react-query";
import {useAuth} from "@/contexts/auth-context.tsx";

export function NotificationDetail() {
    const i18n = useI18n()
    usePageTitle(i18n.notifications.detailPageTitle)
    const {id} = useParams<{id: string}>()

    const {data: notification, isLoading, error} = useNotification(id) as UseQueryResult<NotificationWithData<unknown>, Error>;
    const { user } = useAuth()
    const markRead = useMarkRead()
    const markedRef = useRef(false)

    useEffect(() => {
        if (notification && !notification.read && !markedRef.current) {
            markedRef.current = true
            markRead.mutate(notification.id)
        }
    }, [notification, markRead])

    const referenceRoute = notification ? getNotificationRoute(notification, user?.identityId) : undefined

    return (
        <PageLayout
            variant="detail"
            data-testid="notification-detail-page"
            title={notification?.title ?? i18n.notifications.detailPageTitle}
            icon={Bell}
            backTo={true}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(notification, 'NOTIFICATION') : undefined)}
            description={notification ? (
                <>
                    <Badge variant={getEventTypeBadgeVariant(notification.eventType)}>
                        {getEventTypeLabel(notification.eventType, i18n.notifications)}
                    </Badge>
                    <Badge variant={notification.read ? 'outline' : 'secondary'}>
                        {notification.read ? i18n.notifications.read : i18n.notifications.unread}
                    </Badge>
                </>
            ) : undefined}
        >
            {notification && (
                <>
                    <DetailsCard
                        data-testid="card-notification-detail"
                        title={i18n.notifications.detailTitle}
                        description={i18n.notifications.detailDescription}
                        fields={[
                            {
                                label: i18n.notifications.message,
                                value: <SmallText className="whitespace-pre-wrap" data-testid="notification-message">{notification.message}</SmallText>,
                                fullWidth: true,
                            },
                            {
                                label: i18n.notifications.eventType,
                                value: <Badge variant={getEventTypeBadgeVariant(notification.eventType)}>{getEventTypeLabel(notification.eventType, i18n.notifications)}</Badge>,
                            },
                            {label: i18n.notifications.date, value: formatTimestamp(notification.createdAt)},
                            {
                                label: i18n.notifications.readStatus,
                                value: <Badge variant={notification.read ? 'outline' : 'secondary'}>{notification.read ? i18n.notifications.read : i18n.notifications.unread}</Badge>,
                            },
                        ]}
                    />

                    {referenceRoute && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="items-center gap-2" data-testid="card-title-go-to-reference">
                                    <Icon icon={ExternalLink} size="lg"/>
                                    {i18n.notifications.goToReference}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <EntityCardLink
                                    to={referenceRoute}
                                    icon={ExternalLink}
                                    data-testid="link-referenced-entity"
                                >
                                    {notification.title}
                                </EntityCardLink>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </PageLayout>
    )
}
